import { LIVE_EVENT_CONN_STATE } from '@src/modules/players/shared/interfaces/live-info.interface';
import { JWPlayerConfig, JwProgressiveSource } from '@src/modules/players/shared/interfaces/player-configs.interface';
import axios from 'axios';
import { ERRORED_STATE } from '@src/modules/players/shared/interfaces/error-message.interface';
import { captureFailure } from '@root/private/js/common/sentry';

declare const _playerOptions: JWPlayerConfig;

const setupLiveErrorHandler = () => {
  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const UPDATE_FREQUENCY = 5000;
  const HLS_BUFFER_STALL_WARNING = 334001;
  const HLS_BUFFER_NOT_FOUND = 333404;

  const activeEventId = _playerOptions.x_midibus_evt_id;
  const activeChannelId = _playerOptions.x_midibus_ch_id;
  const activeStreamId = _playerOptions.x_midibus_st_id;

  let isPlayerConfiguring = false;
  let retryCount = 3;
  let retryOnInvalidStream = 3;
  let loadingLayer;

  let secEventId;
  if (typeof _playerOptions.x_midibus_sec_event_id !== 'undefined') {
    secEventId = _playerOptions.x_midibus_sec_event_id;
  }

  /** 메시지를 <br> 기준으로 주/부 2단으로 렌더한다. 종료 화면(live-ended.pug)과 같은 위계다. */
  function renderMessageHtml(message: string): string {
    const parts = message.split('<br>');
    const main = parts[0];
    const sub = parts.slice(1).join('<br>');
    return (
      '<span class="loading--text-main">' + main + '</span>' +
      (sub ? '<span class="loading--text-sub">' + sub + '</span>' : '')
    );
  }

  function setErrorMessage(errorMessage: string) {
    loadingLayer = document.querySelector('#loading--layer');
    const loadingImg = document.querySelector('#loading--icon') as HTMLElement;
    loadingImg.style.display = 'none';
    const errorIcon = document.querySelector(
      '#loading--error-icon',
    ) as HTMLElement;
    errorIcon.style.display = 'flex';

    const errorMessageElement = document.querySelector('#loading--text');
    errorMessageElement.innerHTML = renderMessageHtml(errorMessage);
  }

  function setWaitingMessage(message: string) {
    loadingLayer = document.querySelector('#loading--layer');
    const loadingImg = document.querySelector('#loading--icon') as HTMLElement;
    loadingImg.style.display = 'block';
    const errorIcon = document.querySelector(
      '#loading--error-icon',
    ) as HTMLElement;
    errorIcon.style.display = 'none';

    const messageElement = document.querySelector('#loading--text');
    messageElement.innerHTML = renderMessageHtml(message);

    loadingLayer.style.display = 'flex';
  }

  // 방송이 종료(FINISHED)되지 않은 한 5초마다 상태를 확인하고 재생을 재시도한다.
  // 지터는 동시 시청자가 한꺼번에 끊겼을 때 /live/state 요청이 한 틱에 몰리는 것을
  // 막기 위한 것이라 유지한다(실제 간격 4~6초).
  const WATCHDOG_INTERVAL = 5000;
  const WATCHDOG_JITTER = 0.2;
  const SUSPENDED_MESSAGE =
    '방송이 일시 중단되었습니다.<br>재개되면 자동으로 재생됩니다.';

  // connState 3(FINISHED) 은 콘솔에서 명시적으로 종료한 상태다. 고정키 채널은
  // 종료 후에도 재송출로 새 방송이 시작되므로, 종료를 알리되 자동 재생 안내는
  // 유지한다. 서버가 렌더하는 종료 화면(views/pages/live-ended.pug)과 같은 문구다.
  const ENDED_MESSAGE =
    '방송이 종료되었습니다.<br>다음 방송이 시작되면 자동으로 재생됩니다.';

  // connState 가 PLAYING 인데 플레이어가 실제로 재생으로 넘어가지 못할 때,
  // 몇 초까지 기다렸다가 워치도그로 넘길지. (1초 간격)
  const LAYER_OFF_MAX_TRIES = 10;

  // 연속 "조회 실패" 허용 횟수. 방송 상태와는 무관하며, 이벤트 자체가 없거나
  // 서버가 응답하지 않을 때 무한 폴링을 막는 안전장치다. 5초 간격이므로 약 5분.
  const MAX_WATCHDOG_FAILURES = 60;

  // streamId 교체 감지 후 실제 reload 까지 주는 지터의 최댓값(ms). 방송이 재개되는
  // 순간 대기 중이던 시청자들이 한꺼번에 새로고침하면 SSR/DB 요청이 한 틱에 몰리므로,
  // 0~5초 사이로 흩어서 스파이크를 막는다.
  const RELOAD_JITTER_MAX = 5000;

  let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
  let isWatchdogRunning = false;
  // streamId 교체를 감지해 reload 가 예약된 뒤 true. 한 번 켜지면 끄지 않는다:
  // reload 가 확정된 시점이라 페이지가 곧 교체되고, 되돌릴 이유가 없다.
  let isReloadScheduled = false;
  let watchdogFailureCount = 0;

  function nextWatchdogDelay() {
    const jitter = 1 + (Math.random() * 2 - 1) * WATCHDOG_JITTER;
    return WATCHDOG_INTERVAL * jitter;
  }

  function onVisibilityChange() {
    if (!isWatchdogRunning) return;
    if (document.hidden === false) {
      if (watchdogTimer) {
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
      }
      runPoll();
    }
  }

  function scheduleNextPoll() {
    if (!isWatchdogRunning) return;
    watchdogTimer = setTimeout(() => {
      runPoll();
    }, nextWatchdogDelay());
  }

  function startWatchdog() {
    if (isWatchdogRunning) return;
    isWatchdogRunning = true;
    watchdogFailureCount = 0;
    document.addEventListener('visibilitychange', onVisibilityChange);
    // 첫 폴링은 기다리지 않는다. failover 3회(15초)를 이미 소진한 시점이라
    // 여기서 또 한 주기를 통째로 흘려보내면 복구가 그만큼 늦어진다.
    runPoll();
  }

  function stopWatchdog() {
    if (watchdogTimer) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }
    isWatchdogRunning = false;
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  async function pollForResume() {
    if (!isWatchdogRunning) return;

    if (document.hidden) {
      // 방치된 탭이 서버를 계속 때리지 않도록 폴링을 건너뛴다
      scheduleNextPoll();
      return;
    }

    // requestChannelLiveState 는 조회 실패를 폴백하지 않고 그대로 던진다. 워치도그가
    // 네트워크 오류를 "방송 종료"나 "새 방송 없음"으로 오인하면 감시가 영구
    // 중단되므로, 실패를 구분할 수 있는 형태를 그대로 쓴다.
    let channelState;
    try {
      channelState = await requestChannelLiveState(activeChannelId);
      watchdogFailureCount = 0;
    } catch (e) {
      watchdogFailureCount += 1;
      if (watchdogFailureCount >= MAX_WATCHDOG_FAILURES) {
        // 계속 실패하면 이벤트 자체가 없거나 서버가 응답하지 않는 것이다
        stopWatchdog();
        setErrorMessage('해당 라이브 방송은 재생할 수 없습니다.');
        return;
      }
      scheduleNextPoll();
      return;
    }

    // 고정키(channelId) 채널은 재송출마다 streamId/eventId 가 모두 바뀐다.
    // streamId 가 바뀌었다면 새 방송이 시작된 것이다. eventId 가 바뀌면 통계/이중화/
    // 채팅/보안키가 전부 따라 바뀌므로 프론트에서 값을 갈아끼우지 않고 reload 로
    // SSR 이 전부 다시 계산하게 한다.
    if (channelState.streamId && channelState.streamId !== activeStreamId) {
      stopWatchdog();
      isReloadScheduled = true;
      const delay = Math.random() * RELOAD_JITTER_MAX;
      setTimeout(() => {
        // 정지 화면에 멈추지 않도록 autoplay 를 붙여서 이동한다. 이미 붙어 있으면
        // (buildAutoplayUrl 이 null 을 반환) 그냥 reload 한다.
        const url = buildAutoplayUrl(location);
        if (url) {
          browserNavigation.replace(url);
        } else {
          browserNavigation.reload();
        }
      }, delay);
      return;
    }

    // streamId 가 같은 채 FINISHED 면 아직 재송출 전 "대기중"이다. 종료가 아니므로
    // 워치도그를 멈추지 않는다. 스트림 자체가 없어 load()/play() 재시도는 무의미하고
    // JW error 만 유발하므로 대기 메시지만 유지한 채 다음 주기를 예약한다.
    if (channelState.connState === LIVE_EVENT_CONN_STATE.FINISHED) {
      setWaitingMessage(ENDED_MESSAGE);
      scheduleNextPoll();
      return;
    }

    // 종료가 아니면 상태와 무관하게 매번 재생을 재시도한다.
    // connState 갱신이 실제 송출 재개보다 늦게 반영되는 경우가 있어,
    // PLAYING 을 기다리지 않고 그냥 다시 물려보는 편이 복구가 빠르다.
    // 재생에 성공하면 jwplayer 'play' 핸들러가 stopWatchdog 을 호출한다.
    //
    // load()/play() 는 JW 가 에러 상태일 때 예외를 던질 수 있다. 그대로 두면
    // 아래 scheduleNextPoll() 에 도달하지 못해 감시가 첫 폴링 한 번으로 끝난다.
    // 재생 재시도 실패는 다음 주기에 다시 해보면 되는 일이라 삼켜야 한다.
    try {
      retryCount = 3;
      retryOnInvalidStream = 3;
      isPlayerConfiguring = false;
      jwplayer().load(jwplayer().getPlaylist());
      jwplayer().play();
    } catch (e) {
      console.warn('[live] 재생 재시도 실패, 다음 주기에 재시도합니다', e);
    }

    scheduleNextPoll();
  }

  /**
   * pollForResume 은 async 라 내부에서 예외가 나면 조용히 rejected promise 가 되고
   * 감시 루프가 그대로 끊긴다. 호출부는 반드시 이 래퍼를 쓴다.
   */
  function runPoll() {
    pollForResume().catch((e) => {
      console.warn('[live] 상태 감시 중 오류, 다음 주기에 재시도합니다', e);
      scheduleNextPoll();
    });
  }

  jwplayer().on('play', () => {
    stopWatchdog();
    retryCount = 3;
    retryOnInvalidStream = 3;
    loadingLayer = document.querySelector('#loading--layer');
    loadingLayer.style.display = 'none';
  });

  async function attemptFailover() {
    loadingLayer = document.querySelector('#loading--layer');
    loadingLayer.style.display = 'flex';
    if (isPlayerConfiguring) return;

    if (retryCount > 0) {
      retryCount -= 1;

      isPlayerConfiguring = true;
      await sleep(UPDATE_FREQUENCY);
      getEventState(activeEventId)
        .then(async (eventState) => {
          if (eventState.connState === LIVE_EVENT_CONN_STATE.PLAYINIG) {
            jwplayer().load(jwplayer().getPlaylist());
            jwplayer().play();
            await sleep(500);
            const playerState = jwplayer().getState();

            // @ts-ignore
            if (playerState !== 'error') {
              if (playerState === 'paused') jwplayer().play();
              // OBS 를 껐는데도 channel_stream.connState 가 한동안 PLAYING 으로
              // 남아 있는 경우가 있다. 그러면 이 인터벌이 'buffering' 상태를 무한히
              // 확인만 하며 /live/state 폴링도 워치도그도 시작되지 않는다.
              // (증상: 요청 1회 후 '라이브 방송을 불러오고 있습니다' 화면 고정)
              // 일정 시간 안에 재생으로 넘어가지 못하면 감시로 넘긴다.
              let layerOffTries = 0;
              const setLayerOffInterval = setInterval(() => {
                const state = jwplayer().getState();
                if (state === 'playing' || state === 'idle') {
                  loadingLayer.style.display = 'none';
                  isPlayerConfiguring = false;
                  clearInterval(setLayerOffInterval);
                  return;
                }
                if (state === 'paused') {
                  jwplayer().play();
                }
                layerOffTries += 1;
                if (layerOffTries >= LAYER_OFF_MAX_TRIES) {
                  clearInterval(setLayerOffInterval);
                  isPlayerConfiguring = false;
                  setWaitingMessage(SUSPENDED_MESSAGE);
                  startWatchdog();
                }
              }, 1000);
            } else {
              // invalid stream 이지만 재생중인 경우
              retryCount += 1;
              retryOnInvalidStream -= 1;
              isPlayerConfiguring = false;
              if (retryOnInvalidStream) return attemptFailover();

              setErrorMessage('해당 라이브 방송은 재생할 수 없습니다.');
            }
          } else if (eventState.connState === LIVE_EVENT_CONN_STATE.READY) {
            setWaitingMessage(SUSPENDED_MESSAGE);
            startWatchdog();
          } else {
            // 원래 여기에 `jwplayer().getState() in ['playing','idle']` 분기가
            // 빈 본문으로 있었다. `in` 은 배열 인덱스를 검사해 항상 false 였던
            // 죽은 코드였는데, includes 로 고치자 살아나면서 isPlayerConfiguring 이
            // true 로 굳어 이후 모든 재시도가 막혔다(요청 1회 후 로딩 화면 고정).
            // 종료가 아닌 이상 계속 재시도한다는 정책상 예외 분기 자체가 불필요하다.
            isPlayerConfiguring = false;
            return attemptFailover();
          }
        })
        .catch((err) => {
          isPlayerConfiguring = false;
          return attemptFailover();
        });
    } else {
      // 라이브는 스트림이 흔들릴 때마다 error → failover 가 반복되므로
      // 개별 error 는 보고하지 않는다. failover 를 모두 소진한 이 시점이
      // "시청 불가 확정" 이라 유일하게 보고할 가치가 있는 신호다.
      captureFailure('Live failover exhausted', {
        level: 'error',
        tags: { feature: 'live-playback' },
        extra: {
          eventId: activeEventId,
          secEventId,
          hasSecondary: !!secEventId,
          playerState: jwplayer().getState(),
        },
      });

      if (secEventId) {
        await sleep(UPDATE_FREQUENCY);
        getEventState(secEventId).then(async (eventState) => {
          if (eventState.connState === LIVE_EVENT_CONN_STATE.PLAYINIG) {
            jwplayer().load(jwplayer().getPlaylist());
            jwplayer().play();
            await sleep(3000);
            // 3초뒤 플레이어 상태 확인
            const playerState = jwplayer().getState();
            // @ts-ignore
            if (playerState !== 'error') {
              if (playerState !== 'playing') jwplayer().play();
              loadingLayer.style.display = 'none';
            }
          }
        });
      }

      // 마지막 stream 상태 체크
      getEventState(activeEventId).then(async (eventState) => {
        if (eventState.connState === LIVE_EVENT_CONN_STATE.PLAYINIG) {
          isPlayerConfiguring = true;
          jwplayer().load(jwplayer().getPlaylist());
          await sleep(3000);
          jwplayer().play();
          // @ts-ignore
          if (jwplayer().getState() !== 'error') {
            // valid stream이므로 플레이어 reload 하여 재생
            if (jwplayer().getState() !== 'playing') jwplayer().play();
            loadingLayer.style.display = 'none';
          }
          isPlayerConfiguring = false;

          // @ts-ignore
          if (jwplayer().getState() === 'error') {
            // 복구 시도 후에도 여전히 error 상태면 워치도그로 넘긴다
            setWaitingMessage(SUSPENDED_MESSAGE);
            startWatchdog();
          }
        } else if (eventState.connState === LIVE_EVENT_CONN_STATE.FINISHED) {
          // 고정키(channelId) 채널에서는 FINISHED 가 최종 상태가 아니라 "다음 방송
          // 대기중"이다(재송출 시 streamId/eventId 가 모두 바뀐다). 워치도그를 기동해
          // 채널 기준으로 계속 확인해야 재송출을 새로고침 없이 감지할 수 있다.
          setWaitingMessage(ENDED_MESSAGE);
          startWatchdog();
        } else if (
          eventState.connState === LIVE_EVENT_CONN_STATE.READY ||
          eventState.connState === LIVE_EVENT_CONN_STATE.PAUSED
        ) {
          setWaitingMessage(SUSPENDED_MESSAGE);
          startWatchdog();
        } else {
          // '?key' 붙으면 보안키 재생. 접근 제한 오류 난 경우 검출
          const isSecurePlay = (
            _playerOptions.playlist[0] as JwProgressiveSource
          ).sources[0].file.includes('?key');

          if (isSecurePlay) {
            // 접근 제한은 폴링해도 해결되지 않으므로 워치도그를 기동하지 않는다
            setErrorMessage(
              '인코딩에 실패했거나 접근 제한되어 재생할 수 없습니다.',
            );
          } else {
            setWaitingMessage(SUSPENDED_MESSAGE);
            startWatchdog();
          }
        }
      });
    }
  }

  const showDefaultErrorMessageOnErrored = () => {
    const jwErrorMessageElement = document.getElementsByClassName(
      'jw-error-text jw-reset-text',
    )[0];
    jwErrorMessageElement.innerHTML = `<p>${_playerOptions.message}</p>`;
  };

  jwplayer().on('warning', (evt) => {
    if (_playerOptions.isErrored === true) {
      showDefaultErrorMessageOnErrored();
    }
  });

  jwplayer().on('error', async (evt) => {
    if (_playerOptions.isErrored === true) {
      showDefaultErrorMessageOnErrored();
      return;
    }

    // 워치도그가 도는 동안에는 워치도그 자신의 load()/play() 재시도가 계속
    // error 를 유발한다. 그때마다 failover 를 다시 타면 Sentry 보고가 쌓이고
    // 중복 작업이 생기므로, 감시 중이면 여기서 끝낸다.
    // stopWatchdog() 이후에도 reload 가 실행되기까지는 최대 RELOAD_JITTER_MAX 만큼
    // 대기 창이 남아 있다. 그 사이 도착하는 error 는 isWatchdogRunning 이 이미
    // false 라 가드를 통과해 failover 를 재진입시키고(그리고 retryCount 가 이미
    // 0이면 Sentry 오보고까지 발생시키므로) isReloadScheduled 로도 함께 막는다.
    if (isWatchdogRunning || isReloadScheduled) {
      return;
    }

    // 아직 한 번도 재생된 적 없는 idle 상태 — 시청 시작 전에 이미 송출이
    // 멈춰 있는 경우다. JW 기본 프리뷰는 그대로 두되(로딩 레이어로 덮지 않는다)
    // 감시는 붙여서 송출이 시작되면 자동으로 재생되게 한다.
    if (jwplayer().getState() === 'idle') {
      startWatchdog();
      return;
    }

    const previewImage = document.querySelector(
      '.jw-preview.jw-reset',
    ) as HTMLElement;
    if (previewImage.style['background-image'] === 'url("")') {
      previewImage.style['background-image'] =
        jwplayer().getPlaylist()[0].image;
    }

    setWaitingMessage('라이브 방송을 불러오고 있습니다');

    await attemptFailover();
  });
};

export default setupLiveErrorHandler;

/** 실패를 그대로 던진다. 조회 실패와 "방송 종료"를 구분해야 하는 쪽에서 쓴다. */
export async function requestEventState(eventId: string) {
  const requestUrl = `//${document.location.host}/live/state/${eventId}`;
  const { data } = await axios.get(requestUrl);
  return data;
}

export async function getEventState(eventId: string) {
  try {
    return await requestEventState(eventId);
  } catch (e) {
    return ERRORED_STATE;
  }
}

/**
 * 고정키(channelId) 채널은 재송출마다 streamId/eventId 가 모두 바뀌어
 * eventId 기준 조회(requestEventState)가 원리적으로 새 방송을 찾지 못한다.
 * 채널 기준으로 "현재 방송"을 조회하며, requestEventState 와 같은 이유로
 * 실패를 폴백하지 않고 그대로 던진다 — 워치도그가 네트워크 오류를 "방송 없음"
 * 으로 오인하면 감시가 영구 중단된다.
 */
export async function requestChannelLiveState(channelId: string) {
  const requestUrl = `//${document.location.host}/live/state/channel/${channelId}`;
  const { data } = await axios.get(requestUrl);
  return data;
}

/**
 * 새 방송으로 넘어갈 때 이동할 URL. 이미 autoplay 가 붙어 있으면 null 을 반환해
 * 호출부가 일반 reload 를 하게 한다. 기존 쿼리스트링(보안키 등)과 hash 는 보존한다.
 */
export function buildAutoplayUrl(loc: {
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  if (/[?&]autoplay(=|&|$)/.test(loc.search)) return null;
  const search = loc.search ? loc.search + '&autoplay' : '?autoplay';
  return loc.pathname + search + loc.hash;
}

/**
 * location.reload()/location.replace() 를 감싼 얇은 seam. jsdom 은 `location.reload`
 * 를 own non-configurable/non-writable 속성으로 구현해 두어 테스트에서 직접
 * 스파이/치환할 수 없다(location.replace 도 마찬가지). 이 객체를 통해 호출하면
 * 테스트가 `reload`/`replace` 메서드를 스파이해 실제 navigation 없이 호출 여부를
 * 검증할 수 있다.
 */
export const browserNavigation = {
  reload(): void {
    location.reload();
  },
  replace(url: string): void {
    location.replace(url);
  },
};
